import React, { useState } from 'react';
import {
  Target, Users, MapPin, Calendar, Tags,
  Plus, Trash2, ChevronDown, Settings2, Globe
} from 'lucide-react';

import { AddTargetingRuleModal } from '@/client/components/admin/Promotion/AddTargetingRuleModal';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';

interface TargetingRule {
  id: string;
  name: string;
  type: 'location' | 'demographic' | 'behavior' | 'interest' | 'custom';
  conditions: {
    field: string;
    operator: string;
    value: any;
  }[];
  status: 'active' | 'inactive';
  priority: number;
}

interface EligibilityCriteria {
  id: string;
  name: string;
  description: string;
  type: 'listing' | 'seller' | 'promotion';
  requirements: {
    field: string;
    condition: string;
    value: any;
  }[];
  status: 'active' | 'inactive';
}

const TargetingAndEligibility = () => {
  useBreadcrumbs([
    { label: 'Promotions Management', href: '#' },
    { label: 'Targeting & Eligibility' }
  ]);
  const [activeTab, setActiveTab] = useState<'targeting' | 'eligibility'>('targeting');
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [showCriteriaModal, setShowCriteriaModal] = useState(false);

  const [targetingRules] = useState<TargetingRule[]>([
    {
      id: '1',
      name: 'Premium Hotel Viewers',
      type: 'behavior',
      conditions: [
        {
          field: 'pageViews',
          operator: 'greaterThan',
          value: 5
        },
        {
          field: 'category',
          operator: 'equals',
          value: 'luxury-hotels'
        }
      ],
      status: 'active',
      priority: 1
    }
  ]);

  const [eligibilityCriteria] = useState<EligibilityCriteria[]>([
    {
      id: '1',
      name: 'Premium Listing Requirements',
      description: 'Criteria for premium listing eligibility',
      type: 'listing',
      requirements: [
        {
          field: 'rating',
          condition: 'minimumRating',
          value: 4.5
        },
        {
          field: 'reviews',
          condition: 'minimumReviews',
          value: 10
        }
      ],
      status: 'active'
    }
  ]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-8xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Targeting & Eligibility</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage targeting rules and eligibility criteria
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="sm:hidden">
            <select
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value as 'targeting' | 'eligibility')}
              className="block w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="targeting">Targeting Rules</option>
              <option value="eligibility">Eligibility Criteria</option>
            </select>
          </div>

          <div className="hidden sm:block">
            <nav className="flex space-x-4" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('targeting')}
                className={`px-3 py-2 font-medium text-sm rounded-md ${activeTab === 'targeting'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                <Target className="inline-block h-5 w-5 mr-2" />
                Targeting Rules
              </button>
              <button
                onClick={() => setActiveTab('eligibility')}
                className={`px-3 py-2 font-medium text-sm rounded-md ${activeTab === 'eligibility'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                <Settings2 className="inline-block h-5 w-5 mr-2" />
                Eligibility Criteria
              </button>
            </nav>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0 h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Target className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {targetingRules.filter(r => r.status === 'active').length}
                </h3>
                <p className="text-sm text-gray-500">Active Targeting Rules</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0 h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Settings2 className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {eligibilityCriteria.filter(c => c.status === 'active').length}
                </h3>
                <p className="text-sm text-gray-500">Active Eligibility Rules</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0 h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Globe className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Global</h3>
                <p className="text-sm text-gray-500">Default Rules Active</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="bg-white rounded-lg shadow">
          {activeTab === 'targeting' ? (
            <div>
              {/* Targeting Rules Header */}
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-medium text-gray-900">Targeting Rules</h2>
                  <button
                    onClick={() => setShowRuleModal(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                  >
                    <Plus className="h-5 w-5 mr-2" />
                    Add Rule
                  </button>
                </div>
              </div>

              {/* Targeting Rules List */}
              <div className="divide-y divide-gray-200">
                {targetingRules.map((rule) => (
                  <div key={rule.id} className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">{rule.name}</h3>
                        <p className="mt-1 text-sm text-gray-500">
                          Type: {rule.type.charAt(0).toUpperCase() + rule.type.slice(1)}
                        </p>
                      </div>
                      <div className="flex items-center space-x-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${rule.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                          {rule.status.charAt(0).toUpperCase() + rule.status.slice(1)}
                        </span>
                        <div className="flex items-center">
                          <button className="text-gray-400 hover:text-gray-500">
                            <Settings2 className="h-5 w-5" />
                          </button>
                          <button className="ml-2 text-gray-400 hover:text-gray-500">
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      {rule.conditions.map((condition, index) => (
                        <div key={index} className="bg-gray-50 p-3 rounded-lg">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-700">{condition.field}</span>
                            <span className="text-sm font-medium text-gray-900">
                              {condition.operator} {condition.value}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div>
              {/* Eligibility Criteria Header */}
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-medium text-gray-900">Eligibility Criteria</h2>
                  <button
                    onClick={() => setShowCriteriaModal(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                  >
                    <Plus className="h-5 w-5 mr-2" />
                    Add Criteria
                  </button>
                </div>
              </div>

              {/* Eligibility Criteria List */}
              <div className="divide-y divide-gray-200">
                {eligibilityCriteria.map((criteria) => (
                  <div key={criteria.id} className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">{criteria.name}</h3>
                        <p className="mt-1 text-sm text-gray-500">{criteria.description}</p>
                      </div>
                      <div className="flex items-center space-x-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${criteria.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                          {criteria.status.charAt(0).toUpperCase() + criteria.status.slice(1)}
                        </span>
                        <div className="flex items-center">
                          <button className="text-gray-400 hover:text-gray-500">
                            <Settings2 className="h-5 w-5" />
                          </button>
                          <button className="ml-2 text-gray-400 hover:text-gray-500">
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      {criteria.requirements.map((requirement, index) => (
                        <div key={index} className="bg-gray-50 p-3 rounded-lg">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-700">{requirement.field}</span>
                            <span className="text-sm font-medium text-gray-900">
                              {requirement.condition}: {requirement.value}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Rule Modal */}
      {showRuleModal && (
        <AddTargetingRuleModal
          onClose={() => setShowRuleModal(false)}
          onSave={(rule) => {
            // Handle save
            setShowRuleModal(false);
          }}
        />
      )}

      {/* Add Criteria Modal */}
      {showCriteriaModal && (
        <AddTargetingRuleModal
          onClose={() => setShowCriteriaModal(false)}
          onSave={(criteria) => {
            // Handle save
            setShowCriteriaModal(false);
          }}
        />
      )}
    </div>
  );
};

export default TargetingAndEligibility;